import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  signup, 
  signin, 
  logout, 
  logoutAll,
  requestPasswordReset, 
  resetPassword,
  validateSession, 
  refreshToken 
} from '../api/authApi';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      validate(token);
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const data = await signin(email, password);
    localStorage.setItem('accessToken', data.access_token);
    localStorage.setItem('isAdmin', data.is_admin ? 'true' : 'false');
    localStorage.setItem('userRoles', JSON.stringify(data.user?.roles || []));
    localStorage.setItem('userPermissions', JSON.stringify(data.user?.permissions || []));
    setUser(data.user);
    window.dispatchEvent(new Event('login'));
    return data;
  };

  const hasPermission = (perm) => {
    if (user?.is_admin) return true;
    if (localStorage.getItem('isAdmin') === 'true') return true;
    const perms = user?.permissions || JSON.parse(localStorage.getItem('userPermissions') || '[]');
    return perms.includes(perm);
  };

  const logoutUser = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        await logout(token);
      }
    } catch (error) {
      console.warn('Error during logout:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
      setUser(null);
    }
  };

  const logoutFromAll = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        await logoutAll(token);
      }
    } catch (error) {
      console.warn('Error during logout all:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
      setUser(null);
    }
  };

  const register = async (username, email, password) => {
    try {
      const data = await signup(username, email, password);
      localStorage.setItem('accessToken', data.access_token);
      localStorage.setItem('isAdmin', data.is_admin ? 'true' : 'false');
      setUser(data.user);
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  const validate = async (token) => {
    try {
      const data = await validateSession(token);
      setUser(data.user);
      localStorage.setItem('isAdmin', data.user?.is_admin ? 'true' : 'false');
      localStorage.setItem('userRoles', JSON.stringify(data.user?.roles || []));
      localStorage.setItem('userPermissions', JSON.stringify(data.user?.permissions || []));
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userPermissions');
      setUser(null);
      setLoading(false);
      return false;
    }
  };

  const passwordResetRequest = async (email) => {
    try {
      await requestPasswordReset(email);
      return true;
    } catch (error) {
      console.error('Password reset request error:', error);
      return false;
    }
  };

  const resetUserPassword = async (token, password) => {
    try {
      await resetPassword(token, password);
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      return false;
    }
  };

  const refreshUserToken = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const data = await refreshToken(token);
        localStorage.setItem('accessToken', data.newAccessToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading,
      login,
      hasPermission,
      register, 
      logout: logoutUser,
      logoutFromAll,
      validate,
      passwordResetRequest,
      resetUserPassword,
      refreshUserToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
